import { Search, Handshake, AlertCircle, ChevronRight, FileWarning } from 'lucide-react'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { type OwnerGroup, humanize, initials } from '../../utils/statements'

export function OwnerStatementList({
  owners,
  loading,
  error,
  search,
  onSearchChange,
  selectedOwnerId,
  onSelectOwner,
}: {
  owners: OwnerGroup[]
  loading: boolean
  error: string | null
  search: string
  onSearchChange: (v: string) => void
  selectedOwnerId: string | null
  onSelectOwner: (ownerId: string) => void
}) {
  return (
    <div className="flex min-h-0 w-full flex-col border-r border-border bg-card lg:w-80 lg:shrink-0">
      <div className="border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search owner, email or plate"
            className="pl-9"
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Owners
          </h2>
          <span className="text-xs text-muted-foreground tabular-nums">
            {loading ? '…' : `${owners.length}`}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && owners.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading owners…
          </div>
        ) : owners.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Handshake className="h-6 w-6" />
            No owners match your filters.
          </div>
        ) : (
          <ul>
            {owners.map((o) => {
              const active = o.owner_id === selectedOwnerId
              return (
                <li key={o.owner_id}>
                  <button
                    type="button"
                    onClick={() => onSelectOwner(o.owner_id)}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-border px-3 py-3 text-left transition-colors',
                      active ? 'bg-[hsl(var(--brand-soft))]' : 'hover:bg-accent',
                    )}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-xs font-semibold text-[hsl(var(--brand-soft-foreground))]">
                      {initials(o.owner_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-sm font-medium text-foreground">
                          {o.owner_name || 'Unnamed owner'}
                        </p>
                        {o.any_source_errors && (
                          <FileWarning className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                        )}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {o.owner_email || '—'}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Chip tone="neutral">
                          {o.vehicle_count} vehicle{o.vehicle_count === 1 ? '' : 's'}
                        </Chip>
                        <Chip tone="neutral">
                          {o.statement_total} statement{o.statement_total === 1 ? '' : 's'}
                        </Chip>
                        {o.owner_status && (
                          <Chip tone="brand">{humanize(o.owner_status)}</Chip>
                        )}
                        {o.has_draft_statement && <Chip tone="amber">Draft statement</Chip>}
                      </div>
                    </div>
                    <ChevronRight
                      className={cn(
                        'mt-1 h-4 w-4 shrink-0',
                        active ? 'text-[hsl(var(--brand))]' : 'text-muted-foreground',
                      )}
                    />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
